import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import ChangePassword from '../components/ChangePassword';
import { useAppConfig } from '../hooks/useAppConfig';
import './Dashboard.css';

interface Profession {
  id: number;
  name: string;
  nameAr: string;
}

interface Wilaya {
  id: number;
  code: string;
  name: string;
  nameAr: string;
}

export default function EditProfile() {
  const { t, i18n } = useTranslation();
  const { config: appConfig } = useAppConfig();
  const isArabic = i18n.language === 'ar';

  // Profile state
  const [profile, setProfile] = useState({
    email: '',
    first_name: '',
    last_name: '',
    profession_id: null as number | null,
    profession_other: '',
    structure: '',
    phone: '',
    address: '',
    wilaya_id: null as number | null,
    preferred_language: 'fr',
  });

  // Reference data
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [wilayas, setWilayas] = useState<Wilaya[]>([]);
  const [loadingData, setLoadingData] = useState(true);

  // Messages
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  // Phone verification state
  const [isPhoneVerified, setIsPhoneVerified] = useState(false);
  const [phoneVerifyMode, setPhoneVerifyMode] = useState<'idle' | 'awaiting-code'>('idle');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneVerifyError, setPhoneVerifyError] = useState('');
  const [phoneVerifyInfo, setPhoneVerifyInfo] = useState('');
  const [phoneVerifyLoading, setPhoneVerifyLoading] = useState(false);

  const requestPhoneVerification = async () => {
    setPhoneVerifyError('');
    setPhoneVerifyInfo('');
    setPhoneVerifyLoading(true);
    try {
      const resp = await api.post('/auth/phone/verify-request');
      setPhoneVerifyMode('awaiting-code');
      setPhoneVerifyInfo(t('profile.phoneCodeSent', 'Code SMS envoyé au {{hint}}', { hint: resp.data?.phone_hint || '' }));
    } catch (err: any) {
      setPhoneVerifyError(err?.response?.data?.detail || t('profile.phoneVerifyError', "Échec de l'envoi du code."));
    } finally {
      setPhoneVerifyLoading(false);
    }
  };

  const submitPhoneVerification = async () => {
    if (phoneCode.length !== 6) {
      setPhoneVerifyError(t('auth.otpInvalidLength', 'Le code doit faire 6 chiffres.'));
      return;
    }
    setPhoneVerifyError('');
    setPhoneVerifyLoading(true);
    try {
      await api.post('/auth/phone/verify', { code: phoneCode });
      setIsPhoneVerified(true);
      setPhoneVerifyMode('idle');
      setPhoneCode('');
      setPhoneVerifyInfo(t('profile.phoneVerified', 'Téléphone vérifié.'));
    } catch (err: any) {
      setPhoneVerifyError(err?.response?.data?.detail || t('profile.phoneVerifyError', 'Code invalide.'));
    } finally {
      setPhoneVerifyLoading(false);
    }
  };

  // Fetch profile and reference data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, professionsRes, wilayasRes] = await Promise.all([
          api.get('/user/profile/me'),
          api.get('/professions'),
          api.get('/wilayas')
        ]);

        const userData = profileRes.data;
        setProfile({
          email: userData.email || '',
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          profession_id: userData.profession_id || null,
          profession_other: userData.profession_other || '',
          structure: userData.structure || '',
          phone: userData.phone || '',
          address: userData.address || '',
          wilaya_id: userData.wilaya_id || null,
          preferred_language: userData.preferred_language || 'fr',
        });
        setIsPhoneVerified(Boolean(userData.is_phone_verified));

        setProfessions(professionsRes.data || []);
        setWilayas(wilayasRes.data || []);
      } catch (err) {
        console.error('Error loading profile:', err);
        setErrorMessage(t('profile.errorLoad'));
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      await api.put('/user/profile/update', profile);
      setSuccessMessage(t('profile.updateSuccess'));
    } catch (err) {
      console.error('Update error:', err);
      setErrorMessage(t('profile.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Check if "Autre" profession is selected
  const isOtherProfession = professions.find(p => p.id === profile.profession_id)?.name?.toLowerCase() === 'autre';

  if (loadingData) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="container py-4">
        {/* Page Header - centered with icon matching menu */}
        <div className="dashboard-header text-center mb-4">
          <h1 className="dashboard-title justify-content-center">
            <i className="bi bi-person-circle me-3"></i>
            {t('profile.title')}
          </h1>
          <p className="text-muted">
            {t('profile.subtitle')}
          </p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show mb-4">
            <i className="bi bi-check-circle me-2"></i>
            {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage('')}></button>
          </div>
        )}
        {errorMessage && (
          <div className="alert alert-danger alert-dismissible fade show mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {errorMessage}
            <button type="button" className="btn-close" onClick={() => setErrorMessage('')}></button>
          </div>
        )}

        {/* Personal Information Section */}
        <section className="dashboard-section mb-4">
          <div className="section-header">
            <h2 className="section-title">
              <i className="bi bi-person-badge me-2 text-primary"></i>
              {t('profile.personalInfo')}
            </h2>
          </div>
          <div className="section-body">
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label">{t('profile.firstName')} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="first_name"
                  className="form-control"
                  value={profile.first_name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">{t('profile.lastName')} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  name="last_name"
                  className="form-control"
                  value={profile.last_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label">{t('profile.email')}</label>
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  value={profile.email}
                  onChange={handleInputChange}
                  disabled
                />
                <small className="text-muted">{t('profile.emailNotEditable')}</small>
              </div>

              <div className="col-md-6">
                <label className="form-label d-flex align-items-center gap-2">
                  {t('profile.phone')} <span className="text-danger">*</span>
                  {profile.phone && (
                    isPhoneVerified ? (
                      <span className="badge bg-success">
                        <i className="bi bi-check-circle me-1"></i>
                        {t('profile.phoneVerifiedBadge', 'Vérifié')}
                      </span>
                    ) : (
                      <span className="badge bg-warning text-dark">
                        <i className="bi bi-exclamation-circle me-1"></i>
                        {t('profile.phoneNotVerifiedBadge', 'Non vérifié')}
                      </span>
                    )
                  )}
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="form-control"
                  value={profile.phone}
                  onChange={handleInputChange}
                />
                {profile.phone && !isPhoneVerified && phoneVerifyMode === 'idle' && appConfig.sms_enabled && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm mt-2"
                    onClick={requestPhoneVerification}
                    disabled={phoneVerifyLoading}
                  >
                    <i className="bi bi-shield-check me-1"></i>
                    {t('profile.phoneVerifyAction', 'Vérifier mon numéro')}
                  </button>
                )}
                {phoneVerifyMode === 'awaiting-code' && (
                  <div className="mt-2 p-2 border rounded bg-light">
                    {phoneVerifyInfo && <div className="small text-muted mb-2">{phoneVerifyInfo}</div>}
                    <div className="input-group input-group-sm">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        className="form-control"
                        placeholder="——————"
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={submitPhoneVerification}
                        disabled={phoneVerifyLoading}
                      >
                        {t('profile.phoneVerifySubmit', 'Valider')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => { setPhoneVerifyMode('idle'); setPhoneCode(''); setPhoneVerifyError(''); }}
                      >
                        {t('common.cancel', 'Annuler')}
                      </button>
                    </div>
                  </div>
                )}
                {phoneVerifyError && <div className="text-danger small mt-1">{phoneVerifyError}</div>}
                {phoneVerifyInfo && phoneVerifyMode === 'idle' && isPhoneVerified && (
                  <div className="text-success small mt-1">{phoneVerifyInfo}</div>
                )}
              </div>

              <div className="col-md-6">
                <label className="form-label">{t('profile.wilaya')}</label>
                <select
                  name="wilaya_id"
                  className="form-select"
                  value={profile.wilaya_id || ''}
                  onChange={handleInputChange}
                >
                  <option value="">{t('profile.selectWilaya')}</option>
                  {wilayas.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.code} - {isArabic ? w.nameAr : w.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12">
                <label className="form-label">{t('profile.profession')}</label>
                <div className="profession-chips">
                  {professions.map(p => (
                    <label key={p.id} className={`profession-chip ${profile.profession_id === p.id ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="profession_id"
                        checked={profile.profession_id === p.id}
                        onChange={() => setProfile(prev => ({ ...prev, profession_id: p.id }))}
                      />
                      <span>{isArabic ? p.nameAr : p.name}</span>
                    </label>
                  ))}
                </div>
                {isOtherProfession && (
                  <input
                    type="text"
                    name="profession_other"
                    className="form-control mt-2"
                    placeholder={t('profile.specifyProfession')}
                    value={profile.profession_other}
                    onChange={handleInputChange}
                  />
                )}
              </div>

              <div className="col-12">
                <label className="form-label">{t('profile.structure')}</label>
                <input
                  type="text"
                  name="structure"
                  className="form-control"
                  value={profile.structure}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-12">
                <label className="form-label">{t('profile.address')}</label>
                <input
                  type="text"
                  name="address"
                  className="form-control"
                  value={profile.address}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-md-6">
                <label className="form-label">
                  {t('profile.preferredLanguage', 'Langue de communication')}
                </label>
                <select
                  name="preferred_language"
                  className="form-select"
                  value={profile.preferred_language}
                  onChange={handleInputChange}
                >
                  <option value="fr">Français</option>
                  <option value="ar">العربية</option>
                </select>
                <small className="text-muted">
                  {t('profile.preferredLanguageHelp', 'Langue utilisée pour les emails et SMS de notification.')}
                </small>
              </div>
            </div>
          </div>
          {/* Save Button - normal size */}
          <div className="d-flex justify-content-center">
            <button
              onClick={handleSave}
              className="btn btn-primary px-4"
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2"></i>
                  {t('profile.save')}
                </>
              )}
            </button>
          </div>
        </section>

        

        {/* Change Password Section */}
        <ChangePassword />
      </div>
    </div>
  );
}
